/**
 * Singleton drag-drop event bus.
 *
 * Attaches one set of capture-phase document listeners for the entire app
 * lifetime.  Consumers register via `registerDropHandler`; on a file drop
 * only the *most recently registered* (topmost) handler fires, preventing
 * duplicate uploads when multiple components (e.g. DragAndDrop + an open
 * modal) are mounted simultaneously.
 *
 * All registered state listeners receive isDraggingOver changes so each
 * consumer can independently show/hide its overlay.
 */

type DropCallback = (files: File[]) => void;
type StateCallback = (isDraggingOver: boolean) => void;

// LIFO stack — the last-registered handler handles each drop.
const dropStack: DropCallback[] = [];
const stateListeners = new Set<StateCallback>();

let enterCount = 0;
let initialized = false;

function notifyState(isDraggingOver: boolean) {
  stateListeners.forEach((l) => l(isDraggingOver));
}

function init() {
  if (initialized) return;
  initialized = true;

  const opts = { capture: true } as const;

  document.addEventListener(
    "dragenter",
    (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      enterCount += 1;
      notifyState(true);
    },
    opts,
  );

  document.addEventListener(
    "dragleave",
    (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      enterCount = Math.max(0, enterCount - 1);
      if (enterCount === 0) notifyState(false);
    },
    opts,
  );

  document.addEventListener(
    "dragover",
    (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
    },
    opts,
  );

  document.addEventListener(
    "drop",
    (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      enterCount = 0;
      notifyState(false);
      if (e.dataTransfer.files.length > 0 && dropStack.length > 0) {
        // Only the topmost handler fires — prevents duplicate uploads when
        // multiple hook instances are mounted at the same time.
        dropStack[dropStack.length - 1](Array.from(e.dataTransfer.files));
      }
    },
    opts,
  );
}

/**
 * Register a drop handler and a state listener.
 * Returns a cleanup function that removes both registrations.
 */
export function registerDropHandler(
  onDrop: DropCallback,
  onState: StateCallback,
): () => void {
  init();
  dropStack.push(onDrop);
  stateListeners.add(onState);

  return () => {
    const idx = dropStack.lastIndexOf(onDrop);
    if (idx !== -1) dropStack.splice(idx, 1);
    stateListeners.delete(onState);
  };
}
